/**
 * ProcessSearchPage — отдельный модуль поиска / обогащения (Block 3 + L1).
 *
 * Block L1: добавлен LibraryBar (Save/Load/Delete) для kind=search.
 * Snapshot v1 = {schema_version, dataset_id, selected_column_keys, output_language, tier_key, priority_domains, prom_mode}.
 * apply делает match-by-name, missing columns → warnings (не 500).
 *
 * Block S1: Search v2 foundation — добавлены SearchTierPanel и
 * SearchPriorityDomainsPanel. Snapshot теперь включает tier_key + priority_domains.
 *
 * Block S2: добавлен SearchEstimatePanel — pre-flight credits range preview.
 *
 * UX-J1..J6 (2026-07-27): добавлен ProcessControlBar — единая панель
 * управления (dataset, library, row scope, tier, cost, execution, language).
 * Старые HeaderPanel + ExecutionPanel сохранены как compact view под баром.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useSearchStore } from '@/store/searchStore';
import { extractApiError } from '@/lib/apiError';
import {
  useStartPipeline,
  usePausePipeline,
  useResumePipeline,
  useCancelPipeline,
  useLatestPipelineByDataset,
  usePipelineStatus,
  usePipelineRows,
} from '@/hooks/useGeneration';
import { useSearchResults, type SearchRowResult } from '@/hooks/useSearchResults';
import { useRerunEstimate, useRestartRows, type RerunEstimateRequest } from '@/hooks/useSearchRerun';
import { useSearchEstimate, type SearchEstimateRequest } from '@/hooks/useSearchEstimate';
import { exportSheetsToExcel } from '@/lib/exportExcel';
import { ProcessControlBar, type ProcessExecutionState } from '@/components/process/ProcessControlBar';
import { SearchColumnTagCloud } from '@/components/search/SearchColumnTagCloud';
import { SearchRunStatusPanel } from '@/components/search/SearchRunStatusPanel';
import { SearchPriorityDomainsPanel } from '@/components/search/SearchPriorityDomainsPanel';
import { SearchDataGrid } from '@/components/search/SearchDataGrid';

/** Format a Decimal-as-string credits value: 2 decimals, trim trailing zeros. */
function fmtCredits(s: string): string {
  const n = Number(s);
  if (!isFinite(n)) return s;
  return n.toFixed(2).replace(/\.?0+$/, '') || '0';
}

export function ProcessSearchPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const setDatasetId = useSearchStore((s) => s.setDatasetId);
  const setOutputLanguage = useSearchStore((s) => s.setOutputLanguage);
  const datasetId = useSearchStore((s) => s.datasetId);
  const selectedColumns = useSearchStore((s) => s.selectedColumns);
  const tierKey = useSearchStore((s) => s.tierKey);
  const setTierKey = useSearchStore((s) => s.setTierKey);
  const priorityDomains = useSearchStore((s) => s.priorityDomains);
  const currentPipelineId = useSearchStore((s) => s.currentPipelineId);
  const setCurrentPipelineId = useSearchStore((s) => s.setCurrentPipelineId);
  const isTestRun = useSearchStore((s) => s.isTestRun);
  const setIsTestRun = useSearchStore((s) => s.setIsTestRun);
  const testRunLimit = useSearchStore((s) => s.testRunLimit);
  const setTestRunLimit = useSearchStore((s) => s.setTestRunLimit);
  const overwriteExisting = useSearchStore((s) => s.overwriteExisting);
  const setOverwriteExisting = useSearchStore((s) => s.setOverwriteExisting);

  // P1.4: dataset preview/available-column-names ранее использовались для
  // per-tab library apply — теперь единая workspace-библиотека применяется без
  // фильтрации по колонкам датасета.
  useEffect(() => {
    const dataset = searchParams.get('dataset');
    if (dataset) setDatasetId(dataset);
    const lang = searchParams.get('lang');
    if (lang) setOutputLanguage(lang);
  }, [searchParams, setDatasetId, setOutputLanguage]);

  // === Cost estimate (Phase 0: wire POST /search/estimate → bar Slot 5) ===
  // Pre-flight credits range preview. Pure — no balance hold, no Celery.
  // Refetches when dataset/tier/test-run/priority-domains change (queryKey=body).
  const estimateBody: SearchEstimateRequest | null = datasetId
    ? {
        dataset_id: datasetId,
        tier_key: tierKey,
        is_test_run: isTestRun,
        test_run_limit: isTestRun ? testRunLimit : undefined,
        priority_domains: priorityDomains,
      }
    : null;
  const { data: estimate } = useSearchEstimate(estimateBody, !!datasetId);
  const costEstimate = useMemo(() => {
    if (!estimate) return null;
    const min = fmtCredits(estimate.estimate_min_credits);
    const max = fmtCredits(estimate.estimate_max_credits);
    return min === max ? `${min} cr` : `${min}–${max} cr`;
  }, [estimate]);

  // M2-B (2026-09-15): восстановление прошлого прогона при выборе датасета
  // (мёртвый хук useLatestPipelineByDataset подключён). Повторный поиск сам
  // НЕ запускается — только показ сохранённых строк.
  const { data: latestPipeline } = useLatestPipelineByDataset(datasetId);
  const prevDatasetRef = useRef(datasetId);
  useEffect(() => {
    if (prevDatasetRef.current !== datasetId) {
      prevDatasetRef.current = datasetId;
      // смена датасета: сбрасываем stale pipeline, чтобы не показывать чужой прогон
      setCurrentPipelineId(null);
      // M2-C: выбор строк принадлежит датасету — сбрасываем вместе с ним
      setSelectedRowIds(new Set());
      setConfirmRerun(false);
    }
  }, [datasetId, setCurrentPipelineId]);
  useEffect(() => {
    if (latestPipeline?.id && !currentPipelineId) {
      setCurrentPipelineId(latestPipeline.id);
    }
  }, [latestPipeline, currentPipelineId, setCurrentPipelineId]);

  // M2-B: сохранённые truth-результаты по строкам датасета (base истины)
  const { data: searchResults } = useSearchResults(datasetId);
  const resultsById = useMemo(() => {
    const map = new Map<string, SearchRowResult>();
    for (const r of searchResults?.results ?? []) map.set(r.row_id, r);
    return map;
  }, [searchResults]);

  // === Execution wiring (start/pause/cancel) ===
  const start = useStartPipeline();
  const pause = usePausePipeline();
  const resume = useResumePipeline();
  const cancel = useCancelPipeline();
  const { data: pipeline } = usePipelineStatus(currentPipelineId);
  const queryClient = useQueryClient();

  // Fix 2026-09-18: после ЗАВЕРШЕНИЯ прогона (completed/failed/cancelled)
  // truth-статусы и результаты инвалидируются — без этого React Query
  // (staleTime 60s) показывал статусы, записанные до прогона.
  const invalidationStatusRef = useRef<string | null>(null);
  useEffect(() => {
    const st = pipeline?.status ?? null;
    if (!st || st === invalidationStatusRef.current) return;
    if (st === 'completed' || st === 'failed' || st === 'cancelled') {
      invalidationStatusRef.current = st;
      void queryClient.invalidateQueries({
        queryKey: ['search', 'results', datasetId],
      });
    }
  }, [pipeline?.status, datasetId, queryClient]);

  // === M2-C: повторный поиск выбранных строк (без отдельной страницы) ===
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set());
  const [confirmRerun, setConfirmRerun] = useState(false);
  const rerunEstimate = useRerunEstimate();
  const restartRows = useRestartRows();

  const selectedCount = selectedRowIds.size;
  const rerunBody: RerunEstimateRequest | null =
    datasetId && selectedCount > 0
      ? {
          dataset_id: datasetId,
          row_ids: [...selectedRowIds],
          tier_key: tierKey,
          is_test_run: isTestRun,
          test_run_limit: isTestRun ? testRunLimit : undefined,
          priority_domains: priorityDomains,
        }
      : null;

  function handleRerunEstimate(): void {
    setConfirmRerun(false);
    if (rerunBody) rerunEstimate.mutate(rerunBody);
  }

  function handleRestartRows(): void {
    if (!rerunBody) return;
    // M2-C: request_id генерируется на КАЖДУЮ попытку запуска (не на сессию).
    const request_id = typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : 'req-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10);
    restartRows.mutate(
      { ...rerunBody, request_id },
      {
        onSuccess: () => {
          setSelectedRowIds(new Set());
          setConfirmRerun(false);
          void queryClient.invalidateQueries({
            queryKey: ['search', 'results', datasetId],
          });
        },
      },
    );
  }

  // P1.1: rows for the client-side .xlsx export (same query the grid uses —
  // React Query dedups by key, so no extra request). UX-J2: flattened columns
  // are user-facing only (no provider/model brands).
  const { data: rowsData } = usePipelineRows(currentPipelineId, 200);

  function handleExportExcel(): void {
    const rows = rowsData?.rows ?? [];
    if (rows.length === 0) return;
    const flat = rows.map((r) => {
      const ed = (r.enriched_data ?? null) as {
        identity?: {
          canonical_name?: string;
          brand?: string;
          model?: string;
          part_number?: string;
        };
        // M1 (2026-09-10): уверенность — результат-уровень (по проверенным
        // страницам); identity.confidence (планировщик) больше не экспортируем.
        confidence?: number | null;
        status?: string;
        summary?: string;
        facts?: { key: string; value: string }[];
        sources?: { domain?: string; url?: string }[];
      } | null;
      const ident = ed?.identity;
      const identity =
        ident?.canonical_name ??
        [ident?.brand, ident?.model].filter(Boolean).join(' ') ??
        ident?.part_number ??
        (r.raw_data.name as string) ??
        '';
      const summary =
        ed?.summary ??
        (Array.isArray(ed?.facts) && ed.facts.length > 0
          ? ed.facts.slice(0, 3).map((f) => `${f.key}: ${f.value}`).join(' · ')
          : '');
      return {
        '#': r.row_index,
        [t('search.grid.identity', { defaultValue: 'Identity' })]: identity,
        [t('search.grid.status', { defaultValue: 'Status' })]:
          r.status === 'success' ? ed?.status ?? 'ok' : r.status,
        [t('search.grid.summary', { defaultValue: 'Summary' })]: summary,
        [t('search.grid.sources', { defaultValue: 'Sources' })]: ed?.sources?.length ?? 0,
        [t('search.grid.confidence', { defaultValue: 'Confidence' })]:
          typeof ed?.confidence === 'number' ? ed.confidence : '—',
      };
    });
    exportSheetsToExcel(`search_${(currentPipelineId ?? 'export').slice(0, 8)}`, [
      { name: 'Search', rows: flat },
    ]);
  }

  const handleStart = () => {
    if (!datasetId) return;
    const s = useSearchStore.getState();
    start.mutate(
      {
        dataset_id: datasetId,
        tier_key: tierKey,
        module_keys: ['search_enrichment'],
        output_language: s.outputLanguage,
        selected_columns: selectedColumns,
        is_test_run: s.isTestRun,
        test_run_limit: s.isTestRun ? s.testRunLimit : undefined,
        // §7: false (default) — строки с готовым результатом search_v2
        // пропускаются обычным запуском.
        overwrite_existing: s.overwriteExisting,
        priority_domains: priorityDomains,
      },
      {
        onSuccess: (pr) => {
          setCurrentPipelineId(pr.id);
          void queryClient.invalidateQueries({
            queryKey: ['search', 'results', datasetId],
          });
        },
      },
    );
  };

  // Fix 2026-09-19 (TZ A3): a test run is unambiguous already at the button -
  // the label shows the mode and scope, not just "Run search".
  const startLabel = isTestRun
    ? t('search.execution.testRunBtn', {
        n: testRunLimit,
        defaultValue: `Test run (first ${testRunLimit} rows)`,
      })
    : t('search.execution.run', { defaultValue: 'Run search enrichment' });

  const executionState: ProcessExecutionState = {
    status: pipeline?.status ?? 'idle',
    isStarting: start.isPending,
    canStart: !!datasetId && selectedColumns.length > 0 && !start.isPending,
    startLabel,
    onStart: handleStart,
    onPause: currentPipelineId ? () => pause.mutate(currentPipelineId) : undefined,
    onResume: currentPipelineId ? () => resume.mutate(currentPipelineId) : undefined,
    onCancel: currentPipelineId ? () => cancel.mutate(currentPipelineId) : undefined,
    pausePending: pause.isPending,
    resumePending: resume.isPending,
    cancelPending: cancel.isPending,
    progress:
      pipeline && pipeline.total_rows > 0
        ? { processed: pipeline.processed_rows, total: pipeline.total_rows }
        : undefined,
    // Fix 2026-09-18: тестовый запуск — отдельная семантика прогресса.
    // Scope прогона (2) ≠ размер датасета (7): подпись «Тестовый запуск:
    // 2 из 2 · в датасете 7 строк» рендерит ProcessControlBar.
    isTestRun: pipeline?.is_test_run ?? false,
    datasetTotal: searchResults?.total_rows ?? undefined,
    // P0.1 2026-07-28: пробрасываем ошибку старта И пайплайна в bar (gate no_search_provider_key / 4xx).
    // P1-1 2026-08-03: читаем унифицированный backend-ответ {error:{message}} (main.py), не только legacy detail.
    errorMessage:
      extractApiError(start.error, undefined) ??
      pipeline?.error_message ??
      null,
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold mb-1">
          {t('search.title', { defaultValue: 'Search Enrichment' })}
        </h1>
        <p className="text-sm text-surface-400">
          {t('search.subtitle', {
            defaultValue:
              'Search the web for each row using the configured search providers. Results are saved into enriched data.',
          })}
        </p>
      </div>

      <ProcessControlBar
        datasetId={datasetId}
        setDatasetId={setDatasetId}
        tierKey={tierKey}
        setTierKey={setTierKey}
        testRunLimit={testRunLimit}
        setTestRunLimit={setTestRunLimit}
        isTestRun={isTestRun}
        setIsTestRun={setIsTestRun}
        costEstimate={costEstimate}
        execution={executionState}
        onExportExcel={currentPipelineId ? handleExportExcel : undefined}
      />

      {/* §7 (2026-09-17): toggle затирания готовых результатов — под
          «Запустить поиск» (ProcessControlBar), выключен по умолчанию. */}
      <label className="flex items-start gap-2 text-sm text-surface-200 cursor-pointer select-none">
        <input
          type="checkbox"
          data-testid="overwrite-toggle"
          className="mt-0.5"
          checked={overwriteExisting}
          onChange={(e) => setOverwriteExisting(e.target.checked)}
        />
        <span>
          {t('search.overwrite.label', {
            defaultValue: 'Overwrite existing results',
          })}
          <span className="block text-xs text-surface-400">
            {t('search.overwrite.hint', {
              defaultValue:
                'Off: rows that already have a search result are skipped. On: the whole dataset is re-processed.',
            })}
          </span>
        </span>
      </label>

      {/* P1.2 2026-08-14: одна строка desktop — колонки (col-span-2) + домены
          (col-span-1). Tier/cost уже в ProcessControlBar (там же Start). */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <SearchColumnTagCloud />
        </div>
        <div className="lg:col-span-1">
          <SearchPriorityDomainsPanel />
        </div>
      </div>

      {/* M2-C: панель повторного поиска выбранных строк */}
      {currentPipelineId && selectedCount > 0 && (
        <div className="card p-4 space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm text-surface-200">
              {t('search.rerun.selected', {
                defaultValue: 'Selected rows: {{count}}',
                count: selectedCount,
              })}
            </span>
            <button
              type="button"
              className="btn-secondary inline-flex items-center gap-1.5"
              onClick={handleRerunEstimate}
              disabled={rerunEstimate.isPending}
            >
              {rerunEstimate.isPending
                ? t('search.rerun.estimating', { defaultValue: 'Estimating…' })
                : t('search.rerun.estimate', { defaultValue: 'Estimate' })}
            </button>
            {!confirmRerun ? (
              <button
                type="button"
                className="btn-primary inline-flex items-center gap-1.5"
                disabled
                title={t('search.rerun.confirmHint', {
                  defaultValue: 'Get the estimate first',
                })}
              >
                {t('search.rerun.restart', { defaultValue: 'Restart search' })}
              </button>
            ) : (
              <button
                type="button"
                className="btn-primary inline-flex items-center gap-1.5"
                onClick={handleRestartRows}
                disabled={restartRows.isPending}
              >
                {restartRows.isPending
                  ? t('search.rerun.starting', { defaultValue: 'Starting…' })
                  : t('search.rerun.confirmRestart', { defaultValue: 'Confirm restart' })}
              </button>
            )}
            <button
              type="button"
              className="btn-ghost"
              onClick={() => setSelectedRowIds(new Set())}
            >
              {t('search.rerun.clearSelection', { defaultValue: 'Clear selection' })}
            </button>
          </div>

          {rerunEstimate.data && (
            <div className="text-sm text-surface-300 space-y-1">
              <div>
                <span className="text-surface-400">
                  {t('search.rerun.estimateTitle', { defaultValue: 'Estimate' })}:{' '}
                </span>
                {fmtCredits(rerunEstimate.data.estimate_min_credits) ===
                fmtCredits(rerunEstimate.data.estimate_max_credits)
                  ? fmtCredits(rerunEstimate.data.estimate_min_credits) +
                    ' ' +
                    t('search.rerun.credits', { defaultValue: 'credits' })
                  : fmtCredits(rerunEstimate.data.estimate_min_credits) +
                    '–' +
                    fmtCredits(rerunEstimate.data.estimate_max_credits) +
                    ' ' +
                    t('search.rerun.credits', { defaultValue: 'credits' })}
                {' · '}
                {t('search.rerun.queriesPerPage', {
                  defaultValue: 'up to {{queries}} queries / {{pages}} pages per row',
                  queries: rerunEstimate.data.max_queries_per_row,
                  pages: rerunEstimate.data.max_pages_per_row,
                })}
              </div>
              {rerunEstimate.data.warning && (
                <div className="text-warning">{rerunEstimate.data.warning}</div>
              )}
            </div>
          )}
          {rerunEstimate.error && (
            <div className="text-sm text-danger">
              {extractApiError(rerunEstimate.error, undefined) ??
                t('search.rerun.estimateError', {
                  defaultValue: 'Failed to compute the estimate.',
                })}
            </div>
          )}
          {restartRows.error && (
            <div className="text-sm text-danger">
              {extractApiError(restartRows.error, undefined) ??
                t('search.rerun.restartError', {
                  defaultValue: 'Failed to restart the search.',
                })}
            </div>
          )}
          {restartRows.isSuccess && (
            <div className="text-xs text-surface-400">
              {t('search.rerun.started', {
                defaultValue: 'Rerun started — progress is shown in the table below.',
              })}
            </div>
          )}
        </div>
      )}

      {/* Fix 2026-09-19 (TZ A2): current-run panel - mode/scope/status always
          visible; a test run cannot be mistaken for a full one. */}
      {pipeline && (
        <SearchRunStatusPanel
          pipeline={pipeline}
          datasetTotal={searchResults?.total_rows}
          rows={rowsData?.rows ?? []}
        />
      )}

      <SearchDataGrid
        resultsById={resultsById}
        selectedRowIds={selectedRowIds}
        onSelectedRowIdsChange={setSelectedRowIds}
        scopeRowIds={pipeline?.scope_row_ids ? new Set(pipeline.scope_row_ids) : undefined}
        runActive={
          pipeline?.status === 'running' ||
          pipeline?.status === 'queued' ||
          pipeline?.status === 'paused'
        }
        runShortId={pipeline?.id ? pipeline.id.slice(0, 8) : undefined}
      />
    </div>
  );
}